import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

/** Lightweight client-side pagination control.
 *
 * Computes start/end and emits page-change callbacks. Pure presentational —
 * the parent owns `page` (1-indexed) and `pageSize` state.
 *
 * Hides itself when there is only one page so short tables stay clean.
 */
export function Pagination({ page, pageSize, total, onPageChange, onPageSizeChange, testId = "pagination" }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  if (total === 0) return null;
  if (total <= pageSize && (!onPageSizeChange || pageSize <= 25)) {
    // Still show the "Showing X of Y" hint for transparency
    return (
      <div className="flex items-center justify-between gap-3 px-1 py-2 text-xs text-muted-foreground" data-testid={testId}>
        <span data-testid={`${testId}-summary`}>Showing {start}–{end} of {total}</span>
      </div>
    );
  }

  const sizes = [10, 25, 50, 100];

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-1 py-3 text-xs" data-testid={testId}>
      <span className="text-muted-foreground" data-testid={`${testId}-summary`}>
        Showing {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <label className="flex items-center gap-2 text-muted-foreground">
            Rows per page
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid={`${testId}-page-size`}
            >
              {sizes.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        )}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            data-testid={`${testId}-prev`}
            className="h-7 w-7"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="px-2 text-muted-foreground" data-testid={`${testId}-current`}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            data-testid={`${testId}-next`}
            className="h-7 w-7"
            aria-label="Next page"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
